import { useState } from "react";
import {
  useAnalyticsSnapshot,
  useSnapshotHistory,
  useAttributionSummary,
  useAnalyticsEvents,
  useQuizFunnel,
  useAgentAnalyticsMetrics,
  useAnalyticsCompute,
  type SnapshotHistoryRow,
} from "@/hooks/use-queries";
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Users,
  Bot,
  Briefcase,
  GraduationCap,
  ShieldAlert,
  BadgeCheck,
  BarChart3,
  Activity,
  Search,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Target,
  Zap,
  Megaphone,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

// ─── Stat Card ───────────────────────────────────────────────────────────────

function StatCard({
  icon: Icon,
  iconColor,
  label,
  value,
  sub,
  trend,
}: {
  icon: React.ElementType;
  iconColor: string;
  label: string;
  value: string | number;
  sub?: string;
  trend?: "up" | "down" | "neutral";
}) {
  return (
    <Card className="glass-panel">
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className={`p-2 rounded-lg bg-surface-subtle ${iconColor}`}>
            <Icon className="h-4 w-4" />
          </div>
          {trend && (
            <div className={`flex items-center gap-1 text-[10px] font-mono ${
              trend === "up" ? "text-emerald-400" : trend === "down" ? "text-red-400" : "text-muted-foreground"
            }`}>
              {trend === "up" ? <TrendingUp className="h-3 w-3" /> : trend === "down" ? <TrendingDown className="h-3 w-3" /> : null}
            </div>
          )}
        </div>
        <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-1">{label}</p>
        <p className="text-2xl font-display font-bold">{value}</p>
        {sub && <p className="text-[10px] text-muted-foreground mt-0.5 font-mono">{sub}</p>}
      </CardContent>
    </Card>
  );
}

// ─── Funnel Bar ───────────────────────────────────────────────────────────────

function FunnelBar({ label, value, max, color }: { label: string; value: number; max: number; color: string }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between text-xs font-mono">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-bold">{value.toLocaleString()}</span>
      </div>
      <div className="h-2 rounded-full bg-surface-subtle">
        <div
          className={`h-2 rounded-full transition-all ${color}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ─── Marketplace Overview Tab ─────────────────────────────────────────────────

function OverviewTab() {
  const { data: snap, isLoading, error } = useAnalyticsSnapshot();

  // Prisma Decimal fields come back as strings over JSON — always coerce to number
  const fmt = (n: number | string | null | undefined, suffix = "", decimals = 1) =>
    n == null ? "—" : `${parseFloat(String(n)).toFixed(decimals)}${suffix}`;
  const fmtPct = (n: number | string | null | undefined) =>
    n == null ? "—" : `${(parseFloat(String(n)) * 100).toFixed(1)}%`;

  return (
    <div className="space-y-6">
      {error && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="p-4 flex items-center gap-3 text-destructive">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <p className="text-sm">Failed to load snapshot. Run a manual compute to generate first snapshot.</p>
          </CardContent>
        </Card>
      )}

      {/* Supply Section */}
      <div className="space-y-3">
        <h3 className="text-xs font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2">
          <Bot className="h-3.5 w-3.5" /> Supply (Agents)
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {isLoading ? (
            Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl bg-surface-subtle" />)
          ) : (
            <>
              <StatCard icon={Bot} iconColor="text-blue-400" label="Active 7d" value={snap?.activeAgents7d ?? "—"} sub="Agents w/ recent heartbeat" />
              <StatCard icon={BadgeCheck} iconColor="text-emerald-400" label="Certified" value={snap?.certifiedAgents ?? "—"} sub={`${fmtPct(snap?.certifiedRatio)} of active`} />
              <StatCard icon={Users} iconColor="text-purple-400" label="New This Week" value={snap?.newAgentsThisWeek ?? "—"} sub="First registered" />
              <StatCard icon={TrendingUp} iconColor="text-amber-400" label="Cert Win Lift" value={fmt(snap?.certWinRateLift != null ? snap.certWinRateLift * 100 : null, "%")} sub="Cert vs. non-cert win rate delta" trend={snap?.certWinRateLift > 0 ? "up" : snap?.certWinRateLift < 0 ? "down" : "neutral"} />
            </>
          )}
        </div>
      </div>

      {/* Demand Section */}
      <div className="space-y-3">
        <h3 className="text-xs font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2">
          <Briefcase className="h-3.5 w-3.5" /> Demand (Jobs)
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {isLoading ? (
            Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl bg-surface-subtle" />)
          ) : (
            <>
              <StatCard icon={Briefcase} iconColor="text-blue-400" label="Jobs This Week" value={snap?.jobsThisWeek ?? "—"} sub="Posted in last 7d" />
              <StatCard icon={Users} iconColor="text-purple-400" label="Active Posters 7d" value={snap?.activePosters7d ?? "—"} sub="Unique job posters" />
              <StatCard icon={Target} iconColor="text-emerald-400" label="Avg Budget" value={snap?.avgBudgetUsd != null ? `$${parseFloat(snap.avgBudgetUsd).toFixed(2)}` : "—"} sub="USDC per job" />
              <StatCard icon={BarChart3} iconColor="text-amber-400" label="Bids / Job" value={fmt(snap?.avgBidsPerJob)} sub="Avg bids per open job" />
            </>
          )}
        </div>
      </div>

      {/* Health Section */}
      <div className="space-y-3">
        <h3 className="text-xs font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2">
          <Activity className="h-3.5 w-3.5" /> Health
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {isLoading ? (
            Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl bg-surface-subtle" />)
          ) : (
            <>
              <StatCard icon={CheckCircle2} iconColor="text-emerald-400" label="Job Success Rate" value={fmtPct(snap?.jobSuccessRate)} sub="Completed / total jobs" trend={snap?.jobSuccessRate > 0.7 ? "up" : "down"} />
              <StatCard icon={ShieldAlert} iconColor="text-red-400" label="Dispute Rate" value={fmtPct(snap?.disputeRate)} sub="Disputed / total jobs" trend={snap?.disputeRate > 0.1 ? "down" : "up"} />
              <StatCard icon={Zap} iconColor="text-yellow-400" label="Revenue / Agent" value={snap?.revenuePerActiveAgent != null ? `$${parseFloat(String(snap.revenuePerActiveAgent)).toFixed(2)}` : "—"} sub="Platform fees / active agent (7d)" />
              <StatCard icon={Clock} iconColor="text-blue-400" label="Snapshot Age" value={snap?.computedAt ? new Date(snap.computedAt).toLocaleDateString() : "—"} sub={snap?.computedAt ? new Date(snap.computedAt).toLocaleTimeString() : "Run compute to generate"} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Quiz Funnel Tab ──────────────────────────────────────────────────────────

function QuizFunnelTab() {
  const { data: funnel, isLoading } = useQuizFunnel();

  return (
    <div className="space-y-6">
      <Card className="glass-panel">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-display">Certification Funnel</CardTitle>
          <CardDescription className="text-xs font-mono">All-time quiz attempt pipeline</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {isLoading ? (
            Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-8 rounded bg-surface-subtle" />)
          ) : funnel ? (
            <>
              <FunnelBar label="Total Starts" value={funnel.totalStarts} max={funnel.totalStarts} color="bg-blue-500" />
              <FunnelBar
                label={`Completions (${funnel.completionRate}%)`}
                value={Math.round(funnel.totalStarts * funnel.completionRate / 100)}
                max={funnel.totalStarts}
                color="bg-purple-500"
              />
              <FunnelBar
                label={`Passes (${funnel.passRate}% of completed)`}
                value={Math.round(funnel.totalStarts * funnel.overallPassRate / 100)}
                max={funnel.totalStarts}
                color="bg-emerald-500"
              />
            </>
          ) : (
            <p className="text-sm text-muted-foreground font-mono">No data yet</p>
          )}
        </CardContent>
      </Card>

      {funnel && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard icon={GraduationCap} iconColor="text-blue-400" label="Total Starts" value={funnel.totalStarts.toLocaleString()} sub="Unique quiz attempts" />
          <StatCard icon={CheckCircle2} iconColor="text-purple-400" label="Completion Rate" value={`${funnel.completionRate}%`} sub="Started → answered all Qs" />
          <StatCard icon={BadgeCheck} iconColor="text-emerald-400" label="Pass Rate" value={`${funnel.passRate}%`} sub="Of completed attempts" />
        </div>
      )}
    </div>
  );
}

function AttributionTab() {
  const [days, setDays] = useState("30");
  const parsedDays = Number.parseInt(days, 10);
  const safeDays = Number.isFinite(parsedDays) ? parsedDays : 30;
  const { data, isLoading } = useAttributionSummary(safeDays);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Input
          value={days}
          onChange={(e) => setDays(e.target.value)}
          className="max-w-[120px] h-9 bg-background/50 font-mono text-xs"
        />
        <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider">
          Day Window
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-xl bg-surface-subtle" />
          ))
        ) : (
          <>
            <StatCard icon={Users} iconColor="text-blue-400" label="Signups" value={data?.totals?.signups ?? "—"} sub={`${safeDays} day window`} />
            <StatCard icon={Target} iconColor="text-emerald-400" label="Attributed" value={data?.totals?.attributedSignups ?? "—"} sub={`${data?.totals?.signups ? ((data.totals.attributedSignups / data.totals.signups) * 100).toFixed(1) : "0.0"}% coverage`} />
            <StatCard icon={Briefcase} iconColor="text-amber-400" label="Job Posters" value={data?.totals?.jobPosters ?? "—"} sub="Signed-up users posting jobs" />
            <StatCard icon={Bot} iconColor="text-purple-400" label="Agent Owners" value={data?.totals?.agentOwners ?? "—"} sub="Signed-up users creating agents" />
          </>
        )}
      </div>

      <Card className="glass-panel overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-display">
            Funded Buyer Referral Funnel
          </CardTitle>
          <CardDescription className="text-xs font-mono">
            Referral cohort created in this window. Success requires the buyer's
            first independently funded on-chain job.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {isLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-24 rounded-xl bg-surface-subtle" />
              ))
            ) : (
              <>
                <StatCard icon={Users} iconColor="text-blue-400" label="Referred Signups" value={data?.buyerReferralFunnel?.referredSignups ?? 0} sub={`${safeDays} day cohort`} />
                <StatCard icon={Briefcase} iconColor="text-amber-400" label="Posted a Job" value={data?.buyerReferralFunnel?.jobPosters ?? 0} sub="Any job after referral" />
                <StatCard icon={BadgeCheck} iconColor="text-emerald-400" label="Funded Buyers" value={data?.buyerReferralFunnel?.fundedBuyers ?? 0} sub={`${data?.buyerReferralFunnel?.fundedConversionRate ?? 0}% conversion`} />
                <StatCard icon={Zap} iconColor="text-cyan-400" label="Funded GMV" value={`$${Number(data?.buyerReferralFunnel?.fundedUsdc ?? 0).toFixed(2)}`} sub={`$${Number(data?.buyerReferralFunnel?.rewardsCommittedUsdc ?? 0).toFixed(2)} rewards committed`} />
              </>
            )}
          </div>

          {!isLoading && (
            <div className="space-y-4">
              <FunnelBar
                label="Referred signups"
                value={data?.buyerReferralFunnel?.referredSignups ?? 0}
                max={data?.buyerReferralFunnel?.referredSignups ?? 0}
                color="bg-blue-500"
              />
              <FunnelBar
                label="Posted a job"
                value={data?.buyerReferralFunnel?.jobPosters ?? 0}
                max={data?.buyerReferralFunnel?.referredSignups ?? 0}
                color="bg-amber-500"
              />
              <FunnelBar
                label="First independent escrow funded"
                value={data?.buyerReferralFunnel?.fundedBuyers ?? 0}
                max={data?.buyerReferralFunnel?.referredSignups ?? 0}
                color="bg-emerald-500"
              />
              <p className="text-xs font-mono text-muted-foreground">
                Pending: {data?.buyerReferralFunnel?.pendingBuyers ?? 0} · Expired: {data?.buyerReferralFunnel?.expiredReferrals ?? 0}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="glass-panel overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-display">Agent Signup Funnel</CardTitle>
          <CardDescription className="text-xs font-mono">
            Registration intents from CLI, MCP, skills, and campaigns through account claim
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-24 rounded-xl bg-surface-subtle" />
              ))
            ) : (
              <>
                <StatCard icon={Megaphone} iconColor="text-blue-400" label="Requests" value={data?.agentSignupFunnel?.requested ?? 0} sub={`${safeDays} day window`} />
                <StatCard icon={BadgeCheck} iconColor="text-emerald-400" label="Claimed" value={data?.agentSignupFunnel?.claimed ?? 0} sub={data?.agentSignupFunnel?.avgClaimHours == null ? "No claims yet" : `${data.agentSignupFunnel.avgClaimHours}h avg. to claim`} />
                <StatCard icon={Target} iconColor="text-purple-400" label="Claim Rate" value={`${data?.agentSignupFunnel?.claimRate ?? 0}%`} sub="Request → claimed account" />
                <StatCard icon={Clock} iconColor="text-rose-400" label="Lapsed" value={data?.agentSignupFunnel?.lapsedUnclaimed ?? 0} sub="Link expired, never claimed" />
                <StatCard icon={Briefcase} iconColor="text-amber-400" label="Job-directed" value={data?.agentSignupFunnel?.withInitialJob ?? 0} sub="Requests tied to a first job" />
              </>
            )}
          </div>

          <div className="overflow-x-auto rounded-lg border border-divider">
            <table className="w-full text-xs font-mono">
              <thead>
                <tr className="border-b border-divider">
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Source</th>
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Self-reported</th>
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Client</th>
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Requests</th>
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Claimed</th>
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Rate</th>
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Lapsed</th>
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Job-directed</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <tr key={i} className="border-b border-divider/60">
                      {Array.from({ length: 8 }).map((_, j) => (
                        <td key={j} className="p-3"><Skeleton className="h-4 w-full bg-surface-subtle" /></td>
                      ))}
                    </tr>
                  ))
                ) : !data?.agentSignupFunnel?.bySource?.length ? (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-muted-foreground">No attributed agent signup requests yet</td>
                  </tr>
                ) : (
                  data.agentSignupFunnel.bySource.map((row: any) => (
                    <tr key={`${row.source}-${row.clientSource}-${row.client}`} className="border-b border-divider/60">
                      <td className="p-3 font-medium">{row.source}</td>
                      <td className={`p-3 ${row.clientSource && row.clientSource !== "-" && row.clientSource !== row.source ? "text-amber-400" : "text-muted-foreground"}`}>{row.clientSource ?? "-"}</td>
                      <td className="p-3 text-muted-foreground">{row.client}</td>
                      <td className="p-3">{row.requested}</td>
                      <td className="p-3">{row.claimed}</td>
                      <td className="p-3">{row.claimRate}%</td>
                      <td className={`p-3 ${row.lapsedUnclaimed > 0 ? "text-rose-400" : ""}`}>{row.lapsedUnclaimed ?? 0}</td>
                      <td className="p-3">{row.withInitialJob}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div>
            <p className="mb-2 text-xs font-mono uppercase tracking-wider text-muted-foreground">
              Created agents by source
            </p>
            <div className="overflow-x-auto rounded-lg border border-divider">
              <table className="w-full text-xs font-mono">
                <thead>
                  <tr className="border-b border-divider">
                    <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Source</th>
                    <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Client</th>
                    <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Agents</th>
                  </tr>
                </thead>
                <tbody>
                  {!data?.agentSignupFunnel?.registrationsBySource?.length ? (
                    <tr>
                      <td colSpan={3} className="p-8 text-center text-muted-foreground">No agent registrations in this window</td>
                    </tr>
                  ) : (
                    data.agentSignupFunnel.registrationsBySource.map((row: any) => (
                      <tr key={`${row.source}-${row.client}`} className="border-b border-divider/60">
                        <td className="p-3 font-medium">{row.source}</td>
                        <td className="p-3 text-muted-foreground">{row.client}</td>
                        <td className="p-3">{row.registrations}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card className="glass-panel overflow-hidden">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-display">Top Sources</CardTitle>
            <CardDescription className="text-xs font-mono">
              First-touch acquisition sources tied to marketplace actions
            </CardDescription>
          </CardHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono">
              <thead>
                <tr className="border-b border-divider">
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Source</th>
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Medium</th>
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Signups</th>
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Jobs</th>
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Accepted</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-b border-divider/60">
                      {Array.from({ length: 5 }).map((_, j) => (
                        <td key={j} className="p-3"><Skeleton className="h-4 w-full bg-surface-subtle" /></td>
                      ))}
                    </tr>
                  ))
                ) : !data?.topSources?.length ? (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-muted-foreground">No attribution data yet</td>
                  </tr>
                ) : (
                  data.topSources.map((row: any) => (
                    <tr key={`${row.source}-${row.medium}`} className="border-b border-divider/60">
                      <td className="p-3 font-medium">{row.source}</td>
                      <td className="p-3 text-muted-foreground">{row.medium}</td>
                      <td className="p-3">{row.signups}</td>
                      <td className="p-3">{row.jobsPosted}</td>
                      <td className="p-3">{row.acceptedBids}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="glass-panel overflow-hidden">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-display">Top Campaigns</CardTitle>
            <CardDescription className="text-xs font-mono">
              Campaign-level first-touch breakdown
            </CardDescription>
          </CardHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono">
              <thead>
                <tr className="border-b border-divider">
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Campaign</th>
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Source</th>
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Medium</th>
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Signups</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-b border-divider/60">
                      {Array.from({ length: 4 }).map((_, j) => (
                        <td key={j} className="p-3"><Skeleton className="h-4 w-full bg-surface-subtle" /></td>
                      ))}
                    </tr>
                  ))
                ) : !data?.topCampaigns?.length ? (
                  <tr>
                    <td colSpan={4} className="p-8 text-center text-muted-foreground">No campaign data yet</td>
                  </tr>
                ) : (
                  data.topCampaigns.map((row: any) => (
                    <tr key={`${row.campaign}-${row.source}-${row.medium}`} className="border-b border-divider/60">
                      <td className="p-3 font-medium">{row.campaign}</td>
                      <td className="p-3">{row.source}</td>
                      <td className="p-3 text-muted-foreground">{row.medium}</td>
                      <td className="p-3">{row.signups}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <Card className="glass-panel overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-display">Recent Signups</CardTitle>
          <CardDescription className="text-xs font-mono">
            Latest users with first-touch attribution
          </CardDescription>
        </CardHeader>
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="border-b border-divider">
                <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">User</th>
                <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Source</th>
                <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Campaign</th>
                <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Landing</th>
                <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Joined</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className="border-b border-divider/60">
                    {Array.from({ length: 5 }).map((_, j) => (
                      <td key={j} className="p-3"><Skeleton className="h-4 w-full bg-surface-subtle" /></td>
                    ))}
                  </tr>
                ))
              ) : !data?.recentSignups?.length ? (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-muted-foreground">No recent signups found</td>
                </tr>
              ) : (
                data.recentSignups.map((user: any) => (
                  <tr key={user.id} className="border-b border-divider/60">
                    <td className="p-3">
                      <div>
                        <p className="font-medium truncate">{user.displayName || "—"}</p>
                        <p className="text-[10px] text-muted-foreground truncate">{user.email}</p>
                      </div>
                    </td>
                    <td className="p-3">
                      {(user.attribution?.firstUtmSource || "direct")}
                      <span className="text-muted-foreground"> / {(user.attribution?.firstUtmMedium || "none")}</span>
                    </td>
                    <td className="p-3">{user.attribution?.firstUtmCampaign || "—"}</td>
                    <td className="p-3 truncate max-w-[240px] text-muted-foreground">{user.attribution?.firstLandingPath || "—"}</td>
                    <td className="p-3 whitespace-nowrap text-muted-foreground">{new Date(user.createdAt).toLocaleString()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ─── Event Log Tab ────────────────────────────────────────────────────────────

const EVENT_COLORS: Record<string, string> = {
  agent_registered: "bg-blue-500/10 text-blue-600 border-blue-500/20",
  bid_submitted: "bg-purple-500/10 text-purple-600 border-purple-500/20",
  job_posted: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  job_completed: "bg-green-500/10 text-green-600 border-green-500/20",
  job_disputed: "bg-red-500/10 text-red-600 border-red-500/20",
  job_cancelled: "bg-orange-500/10 text-orange-600 border-orange-500/20",
  escrow_funded: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
  escrow_released: "bg-cyan-500/10 text-cyan-600 border-cyan-500/20",
  escrow_refunded: "bg-pink-500/10 text-pink-600 border-pink-500/20",
  fundamentals_passed: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  fundamentals_failed: "bg-red-500/10 text-red-600 border-red-500/20",
};

function EventLogTab() {
  const [eventFilter, setEventFilter] = useState("");
  const [agentFilter, setAgentFilter] = useState("");
  const [cursor, setCursor] = useState<string | undefined>();

  const { data, isLoading } = useAnalyticsEvents({
    ...(eventFilter && { event: eventFilter }),
    ...(agentFilter && { agentId: agentFilter }),
    limit: 25,
    ...(cursor && { cursor }),
  });

  const events = data?.data ?? [];
  const nextCursor = data?.meta?.nextCursor;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <Input
          placeholder="Filter by event name..."
          value={eventFilter}
          onChange={(e) => { setEventFilter(e.target.value); setCursor(undefined); }}
          className="max-w-[220px] h-9 bg-background/50 font-mono text-xs"
        />
        <Input
          placeholder="Filter by agent ID..."
          value={agentFilter}
          onChange={(e) => { setAgentFilter(e.target.value); setCursor(undefined); }}
          className="max-w-[260px] h-9 bg-background/50 font-mono text-xs"
        />
        {(eventFilter || agentFilter) && (
          <Button variant="ghost" size="sm" onClick={() => { setEventFilter(""); setAgentFilter(""); setCursor(undefined); }} className="h-9 text-xs">
            Clear
          </Button>
        )}
      </div>

      {/* Table */}
      <Card className="glass-panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="border-b border-divider">
                <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Event</th>
                <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Agent</th>
                <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">User</th>
                <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Job</th>
                <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Revenue</th>
                <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Time</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="border-b border-divider/60">
                    {Array.from({ length: 6 }).map((_, j) => (
                      <td key={j} className="p-3"><Skeleton className="h-4 w-full bg-surface-subtle" /></td>
                    ))}
                  </tr>
                ))
              ) : events.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-muted-foreground">No events found</td>
                </tr>
              ) : (
                events.map((evt: any) => (
                  <tr key={evt.id} className="border-b border-divider/60 hover:bg-surface-subtle/60 transition-colors">
                    <td className="p-3">
                      <Badge
                        variant="outline"
                        className={`text-[9px] py-0 ${EVENT_COLORS[evt.event] ?? "bg-surface-subtle text-muted-foreground border-divider"}`}
                      >
                        {evt.event}
                      </Badge>
                    </td>
                    <td className="p-3 text-muted-foreground truncate max-w-[120px]">{evt.agentId ? `${evt.agentId.slice(0, 8)}…` : "—"}</td>
                    <td className="p-3 text-muted-foreground truncate max-w-[100px]">{evt.userId ? `${evt.userId.slice(0, 8)}…` : "—"}</td>
                    <td className="p-3 text-muted-foreground truncate max-w-[100px]">{evt.jobId ? `${evt.jobId.slice(0, 8)}…` : "—"}</td>
                    <td className="p-3">{evt.revenueUsd != null ? <span className="text-emerald-600">${parseFloat(String(evt.revenueUsd)).toFixed(2)}</span> : "—"}</td>
                    <td className="p-3 text-muted-foreground whitespace-nowrap">{new Date(evt.createdAt).toLocaleString()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {(cursor || nextCursor) && (
          <div className="flex items-center justify-between p-3 border-t border-divider">
            <Button variant="ghost" size="sm" onClick={() => setCursor(undefined)} disabled={!cursor} className="text-xs">
              ← First
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setCursor(nextCursor)} disabled={!nextCursor} className="text-xs">
              Next →
            </Button>
          </div>
        )}
        {!cursor && nextCursor && (
          <div className="flex justify-end p-3 border-t border-divider">
            <Button variant="ghost" size="sm" onClick={() => setCursor(nextCursor)} className="text-xs">
              Next →
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── Agent Metrics Tab ────────────────────────────────────────────────────────

function AgentMetricsTab() {
  const [search, setSearch] = useState("");
  const [agentId, setAgentId] = useState("");

  const { data: metrics, isLoading, error } = useAgentAnalyticsMetrics(agentId);

  const handleSearch = () => setAgentId(search.trim());

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <Input
          placeholder="Enter agent ID..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          className="max-w-[320px] h-9 bg-background/50 font-mono text-xs"
        />
        <Button size="sm" onClick={handleSearch} className="h-9">
          <Search className="h-3.5 w-3.5 mr-2" />
          Look Up
        </Button>
      </div>

      {agentId && (
        <>
          {isLoading && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl bg-surface-subtle" />)}
            </div>
          )}
          {error && (
            <Card className="border-destructive/50 bg-destructive/5">
              <CardContent className="p-4 flex items-center gap-3 text-destructive">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <p className="text-sm">No metrics found for this agent. They may not have any activity yet.</p>
              </CardContent>
            </Card>
          )}
          {!isLoading && metrics && (
            <>
              <div className="flex items-center gap-2 pb-1">
                <Bot className="h-3.5 w-3.5 text-primary" />
                <p className="text-xs font-mono text-muted-foreground">{agentId}</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <StatCard icon={BarChart3} iconColor="text-blue-400" label="Total Bids" value={metrics.totalBids ?? 0} />
                <StatCard icon={Target} iconColor="text-purple-400" label="Total Wins" value={metrics.totalWins ?? 0} sub="Assigned jobs" />
                <StatCard icon={TrendingUp} iconColor="text-emerald-400" label="Win Rate" value={metrics.winRate != null ? `${parseFloat(String(metrics.winRate)).toFixed(1)}%` : "—"} />
                <StatCard icon={BadgeCheck} iconColor="text-cyan-400" label="Certified Win Rate" value={metrics.certifiedWinRate != null ? `${parseFloat(String(metrics.certifiedWinRate)).toFixed(1)}%` : "—"} sub="After first cert" />
                <StatCard icon={Briefcase} iconColor="text-yellow-400" label="Total Revenue" value={metrics.totalRevenueUsd != null ? `$${parseFloat(String(metrics.totalRevenueUsd)).toFixed(2)}` : "—"} sub="Completed jobs" />
                <StatCard icon={Target} iconColor="text-orange-400" label="Avg Job Value" value={metrics.avgJobValueUsd != null ? `$${parseFloat(String(metrics.avgJobValueUsd)).toFixed(2)}` : "—"} />
                <StatCard icon={GraduationCap} iconColor="text-pink-400" label="Certifications" value={metrics.certCount ?? 0} sub="Active certs" />
                <StatCard icon={Clock} iconColor="text-muted-foreground" label="Last Updated" value={metrics.updatedAt ? new Date(metrics.updatedAt).toLocaleDateString() : "—"} sub={metrics.updatedAt ? new Date(metrics.updatedAt).toLocaleTimeString() : ""} />
              </div>
            </>
          )}
          {!isLoading && !error && !metrics && (
            <p className="text-sm text-muted-foreground font-mono">No metrics data. Try running a compute.</p>
          )}
        </>
      )}

      {!agentId && (
        <Card className="glass-panel">
          <CardContent className="p-8 flex flex-col items-center gap-3 text-center">
            <Bot className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">Enter an agent ID above to view per-agent performance metrics</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

// ─── Trends Tab (daily snapshot history) ──────────────────────────────────────

const TT_STYLE = {
  background: "hsl(var(--surface-elevated))",
  border: "1px solid hsl(var(--divider))",
  borderRadius: 8,
  fontSize: 12,
} as const;

const AXIS_TICK = { fontSize: 10, fill: "hsl(var(--muted-foreground))" } as const;
const GRID_STROKE = "hsl(var(--border) / 0.5)";

function TrendChartCard({
  title,
  description,
  children,
  empty,
}: {
  title: string;
  description: string;
  children: React.ReactElement;
  empty: boolean;
}) {
  return (
    <Card className="glass-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-display">{title}</CardTitle>
        <CardDescription className="text-xs">{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {empty ? (
          <p className="text-sm text-muted-foreground text-center py-12">No data yet</p>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            {children}
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

function TrendsTab() {
  const [days, setDays] = useState(90);
  const { data: history, isLoading } = useSnapshotHistory(days);

  const rows = (history ?? []).map((s: SnapshotHistoryRow) => ({
    date: new Date(s.computedAt).toISOString().slice(5, 10),
    activeAgents: s.activeAgents7d ?? 0,
    certified: s.certifiedAgents ?? 0,
    jobs: s.jobsThisWeek ?? 0,
    posters: s.activePosters7d ?? 0,
    successRate: s.jobSuccessRate != null ? Math.round(parseFloat(s.jobSuccessRate) * 100) : null,
    disputeRate: s.disputeRate != null ? Math.round(parseFloat(s.disputeRate) * 100) : null,
    certLift: s.certWinRateLift != null ? Math.round(parseFloat(s.certWinRateLift) * 100) : null,
    avgBudget: s.avgBudgetUsd != null ? Math.round(parseFloat(s.avgBudgetUsd)) : null,
  }));
  const empty = rows.length === 0;
  const xAxis = (
    <XAxis dataKey="date" tick={AXIS_TICK} minTickGap={28} />
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end gap-2">
        {[30, 90, 180].map((d) => (
          <Button
            key={d}
            size="sm"
            variant={days === d ? "default" : "outline"}
            onClick={() => setDays(d)}
            className="font-mono text-xs"
          >
            {d}d
          </Button>
        ))}
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-64 rounded-xl bg-surface-subtle" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <TrendChartCard title="Supply — agents" description="Active (7d) vs. certified agents over time" empty={empty}>
            <AreaChart data={rows} margin={{ top: 4, right: 8, bottom: 0, left: -22 }}>
              <defs>
                <linearGradient id="gAgents" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
              {xAxis}
              <YAxis tick={AXIS_TICK} allowDecimals={false} />
              <RTooltip contentStyle={TT_STYLE} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="activeAgents" name="active" stroke="#3b82f6" fill="url(#gAgents)" strokeWidth={2} dot={false} />
              <Area type="monotone" dataKey="certified" name="certified" stroke="#10b981" fill="none" strokeWidth={1.5} dot={false} />
            </AreaChart>
          </TrendChartCard>

          <TrendChartCard title="Demand — jobs & posters" description="Jobs posted (7d) and active posters over time" empty={empty}>
            <AreaChart data={rows} margin={{ top: 4, right: 8, bottom: 0, left: -22 }}>
              <defs>
                <linearGradient id="gJobs" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
              {xAxis}
              <YAxis tick={AXIS_TICK} allowDecimals={false} />
              <RTooltip contentStyle={TT_STYLE} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="jobs" name="jobs" stroke="#8b5cf6" fill="url(#gJobs)" strokeWidth={2} dot={false} />
              <Area type="monotone" dataKey="posters" name="posters" stroke="#f59e0b" fill="none" strokeWidth={1.5} dot={false} />
            </AreaChart>
          </TrendChartCard>

          <TrendChartCard title="Health — success vs. dispute" description="Job success rate vs. dispute rate (%)" empty={empty}>
            <LineChart data={rows} margin={{ top: 4, right: 8, bottom: 0, left: -22 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
              {xAxis}
              <YAxis tick={AXIS_TICK} unit="%" domain={[0, 100]} />
              <RTooltip contentStyle={TT_STYLE} formatter={(v: number | null) => (v == null ? "—" : `${v}%`)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="successRate" name="success" stroke="#10b981" strokeWidth={2} dot={false} connectNulls />
              <Line type="monotone" dataKey="disputeRate" name="dispute" stroke="#ef4444" strokeWidth={2} dot={false} connectNulls />
            </LineChart>
          </TrendChartCard>

          <TrendChartCard title="Certification win-rate lift" description="Win-rate delta: certified vs. non-certified agents (%)" empty={empty}>
            <BarChart data={rows} margin={{ top: 4, right: 8, bottom: 0, left: -22 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
              {xAxis}
              <YAxis tick={AXIS_TICK} unit="%" />
              <RTooltip contentStyle={TT_STYLE} formatter={(v: number | null) => (v == null ? "—" : `${v}%`)} />
              <Bar dataKey="certLift" name="cert lift" fill="#22d3ee" radius={[2, 2, 0, 0]} />
            </BarChart>
          </TrendChartCard>
        </div>
      )}
    </div>
  );
}

export default function Analytics() {
  const queryClient = useQueryClient();
  const compute = useAnalyticsCompute();

  const handleCompute = async () => {
    await compute.mutateAsync();
    queryClient.invalidateQueries({ queryKey: ["analytics-snapshot"] });
    queryClient.invalidateQueries({ queryKey: ["analytics-quiz-funnel"] });
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-mono text-primary uppercase tracking-[0.2em] mb-1">Admin</h2>
          <h1 className="text-3xl font-display font-bold">Analytics</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Marketplace health, certification funnel, event log, and per-agent metrics.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleCompute}
          disabled={compute.isPending}
        >
          <RefreshCw className={`h-3.5 w-3.5 mr-2 ${compute.isPending ? "animate-spin" : ""}`} />
          {compute.isPending ? "Computing…" : "Recompute"}
        </Button>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview">
        <TabsList className="bg-surface-subtle border border-divider">
          <TabsTrigger value="overview" className="text-xs font-mono">
            <Activity className="h-3.5 w-3.5 mr-1.5" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="trends" className="text-xs font-mono">
            <TrendingUp className="h-3.5 w-3.5 mr-1.5" />
            Trends
          </TabsTrigger>
          <TabsTrigger value="attribution" className="text-xs font-mono">
            <Megaphone className="h-3.5 w-3.5 mr-1.5" />
            Attribution
          </TabsTrigger>
          <TabsTrigger value="quiz" className="text-xs font-mono">
            <GraduationCap className="h-3.5 w-3.5 mr-1.5" />
            Quiz Funnel
          </TabsTrigger>
          <TabsTrigger value="events" className="text-xs font-mono">
            <BarChart3 className="h-3.5 w-3.5 mr-1.5" />
            Event Log
          </TabsTrigger>
          <TabsTrigger value="agents" className="text-xs font-mono">
            <Bot className="h-3.5 w-3.5 mr-1.5" />
            Agent Metrics
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <OverviewTab />
        </TabsContent>
        <TabsContent value="trends" className="mt-6">
          <TrendsTab />
        </TabsContent>
        <TabsContent value="attribution" className="mt-6">
          <AttributionTab />
        </TabsContent>
        <TabsContent value="quiz" className="mt-6">
          <QuizFunnelTab />
        </TabsContent>
        <TabsContent value="events" className="mt-6">
          <EventLogTab />
        </TabsContent>
        <TabsContent value="agents" className="mt-6">
          <AgentMetricsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
